import React from "react";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert } from "@mui/material";
import { LoadingButton, RHFTextField } from "@components";
import { useResetPassword } from "@api/v1/hooks/useResetPassword";

const FormStyled = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

interface ResetPasswordFormFields {
  email: string;
}

const LoadingButtonStyled = styled(LoadingButton)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

export const ResetPasswordPageForm = () => {
  const { t } = useTranslation();

  const resetPasswordSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("form.errors.wrongEmailFormat"))
      .required(t("form.errors.required")),
  });

  const { handleSubmit, control, getValues } = useForm<ResetPasswordFormFields>(
    {
      resolver: yupResolver(resetPasswordSchema),
      defaultValues: {
        email: "",
      },
    }
  );

  const { mutateAsync: resetPassword, isSuccess } = useResetPassword();

  const onSubmit: SubmitHandler<ResetPasswordFormFields> = async (fields) => {
    await resetPassword({ email: fields.email });
  };

  const currentEmail = getValues().email;

  return (
    <FormStyled onSubmit={handleSubmit(onSubmit)}>
      {isSuccess && (
        <Alert severity="success">
          {t("resetPasswordPage.form.successfulMessage", {
            email: currentEmail,
          })}
        </Alert>
      )}

      <RHFTextField
        fullWidth
        label={t("resetPasswordPage.form.email")}
        name="email"
        type="email"
        variant="outlined"
        size="small"
        control={control}
      />

      <LoadingButtonStyled
        fullWidth
        type="submit"
        variant="primary"
        isFetching={false}
      >
        {t("resetPasswordPage.form.submitButton")}
      </LoadingButtonStyled>
    </FormStyled>
  );
};
